/**
 * AI 相关 API 服务
 */
import apiClient from './client';
import type {
  AIJobCreateResponse,
  AIJobStatusPayload,
  AIJobsListResponse,
  AIModelResponse,
  GenerateImageDto,
  GenerateVideoDto,
  ImageEditOperation,
  RawModelDefinition,
} from '@/types/ai';
import type { ImageEditRequestTarget } from '@/types/edit-image';
import type { ImageUpscaleRequestTarget } from '@/lib/storyboard-image-upscale';
import type { VoiceApiItem, VoiceLabels, VoicesResponse } from '@/types/voice';
import { isChineseVoice } from '@/types/voice';

type ActiveOrgRequest = {
  activeOrgId?: string | null;
  signal?: AbortSignal;
};

function withActiveOrgHeader(
  request?: ActiveOrgRequest
): { headers?: Record<string, string>; signal?: AbortSignal } | undefined {
  const activeOrgId = request?.activeOrgId;
  const hasActiveOrgOverride =
    !!request && Object.prototype.hasOwnProperty.call(request, 'activeOrgId');
  const signal = request?.signal;

  if (!hasActiveOrgOverride && !signal) {
    return undefined;
  }

  return {
    ...(hasActiveOrgOverride ? { headers: { 'X-Active-Org-Id': activeOrgId ?? '' } } : {}),
    ...(signal ? { signal } : {}),
  };
}

const toVoiceLabels = (labels: unknown): VoiceLabels | undefined => {
  if (!labels || typeof labels !== 'object') return undefined;
  const raw = labels as Record<string, unknown>;
  return {
    accent: typeof raw.accent === 'string' ? raw.accent : undefined,
    description: typeof raw.description === 'string' ? raw.description : undefined,
    age: typeof raw.age === 'string' ? raw.age : undefined,
    gender: typeof raw.gender === 'string' ? raw.gender : undefined,
    useCase: typeof raw.useCase === 'string' ? raw.useCase : undefined,
    language: typeof raw.language === 'string' ? raw.language : undefined,
  };
};

const normalizeVoiceItem = (voice: unknown): VoiceApiItem | null => {
  if (!voice || typeof voice !== 'object') return null;
  const raw = voice as Record<string, unknown>;
  const voiceId = typeof raw.voiceId === 'string' ? raw.voiceId : '';
  const name = typeof raw.name === 'string' ? raw.name : '';
  if (!voiceId || !name) return null;

  const verifiedLanguages = Array.isArray(raw.verifiedLanguages)
    ? (raw.verifiedLanguages as Array<Record<string, unknown>>).map((lang) => ({
        language: typeof lang.language === 'string' ? lang.language : '',
        languageName: typeof lang.languageName === 'string' ? lang.languageName : '',
      }))
    : undefined;

  return {
    voiceId,
    name,
    nameCn: typeof raw.nameCn === 'string' ? raw.nameCn : undefined,
    category: typeof raw.category === 'string' ? raw.category : undefined,
    description: typeof raw.description === 'string' ? raw.description : undefined,
    descriptionCn: typeof raw.descriptionCn === 'string' ? raw.descriptionCn : undefined,
    labels: toVoiceLabels(raw.labels),
    previewUrl: typeof raw.previewUrl === 'string' ? raw.previewUrl : undefined,
    verifiedLanguages,
  };
};

// ─── /ai/model-definitions 适配器 ────────────────────────────────────────────

const VIDEO_MODEL_TYPE = 3;
const UPSCALE_MODEL_TYPE = 6;
const IMAGE_UPSCALE_MODEL_TYPE = 7;

/**
 * 将视频模型的 generationMode 参数（registry 富结构）转换为 capabilities.videoType
 * 兼容 capability-options.ts 期望的 { supported, values: string[], default } 结构
 */
function extractVideoType(raw: unknown): {
  supported: boolean;
  values: string[];
  default?: string;
} {
  if (!raw || typeof raw !== 'object') return { supported: false, values: [] };
  const r = raw as Record<string, unknown>;
  const rawValues = Array.isArray(r.values) ? r.values : [];
  const values = rawValues
    .map((v) => (typeof v === 'string' ? v : (v as Record<string, unknown>)?.value))
    .filter((v): v is string => typeof v === 'string' && v.length > 0);
  return {
    supported: r.supported !== false,
    values,
    ...(typeof r.default === 'string' ? { default: r.default } : {}),
  };
}

/**
 * 将新 /ai/model-definitions 接口的 RawModelDefinition
 * 适配为旧 AIModelResponse 结构，保持 capability-options.ts 不变
 *
 * 映射规则：
 * - 视频(3)：parameters.generationMode → capabilities.videoType（结构转换 + 改名）
 * - 图片(2)：parameters.generationMode → capabilities.generationMode（透传，备用）
 * - 视频超清(6)：parameters.resolution → capabilities.upscaleResolution
 * - 图片超清(7)：parameters.scale 透传为 capabilities.scale
 * - capabilities.inputReference 直接透传
 */
export function adaptModelDefinition(def: RawModelDefinition): AIModelResponse {
  const merged: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(def.parameters ?? {})) {
    if (key === 'generationMode') {
      if (def.modelType === VIDEO_MODEL_TYPE) {
        merged['videoType'] = extractVideoType(value);
      } else {
        merged['generationMode'] = value;
      }
    } else if (key === 'resolution' && def.modelType === UPSCALE_MODEL_TYPE) {
      merged['upscaleResolution'] = value;
    } else {
      merged[key] = value;
    }
  }

  const caps = def.capabilities as Record<string, unknown>;
  if (caps?.inputReference) merged['inputReference'] = caps.inputReference;
  // 提示词约束在 capabilities.prompt（非 parameters），需透传给输入框软/硬阈值
  if (caps?.prompt && typeof caps.prompt === 'object') merged['prompt'] = caps.prompt;
  // 按 generation_mode 的任务级覆写（aspectRatio / durationMode / inputReference）
  if (caps?.byMode && typeof caps.byMode === 'object') merged['byMode'] = caps.byMode;
  if (Array.isArray(caps?.features)) merged['features'] = caps.features;
  if (caps?.imageEdit && typeof caps.imageEdit === 'object') merged['imageEdit'] = caps.imageEdit;
  if (def.modelType === UPSCALE_MODEL_TYPE) {
    if (caps?.inputVideo) merged['inputVideo'] = caps.inputVideo;
  }
  if (def.modelType === IMAGE_UPSCALE_MODEL_TYPE) {
    if (caps?.inputImage) merged['inputImage'] = caps.inputImage;
  }

  return {
    modelId: def.modelId,
    modelName: def.modelName,
    displayName: def.displayName,
    description: def.description ?? null,
    modelType: def.modelType,
    isBeta: def.isBeta,
    orderIndex: def.orderIndex ?? 0,
    provider: 0,
    costPerUnit: 0,
    unitType: 0,
    creditsPerUnit: 0,
    config: {},
    capabilities: merged,
  };
}

export const aiApi = {
  /**
   * 获取 AI 模型列表
   * GET /api/v1/ai/model-definitions（新接口）
   * @param modelType 模型类型（1=文本 2=图片 3=视频 4=音效 5=配音 6=视频超清 7=图片超清）
   */
  async getModels(modelType?: number, request?: ActiveOrgRequest): Promise<AIModelResponse[]> {
    const defs = (await apiClient.get('/ai/model-definitions', {
      params: { modelType },
      ...withActiveOrgHeader(request),
    })) as RawModelDefinition[];
    return defs.map(adaptModelDefinition);
  },

  async submitImageEdit(
    payload: {
      operation: ImageEditOperation;
      model: string;
      target: ImageEditRequestTarget;
      mask: Blob;
      prompt?: string;
    },
    request?: ActiveOrgRequest
  ): Promise<AIJobCreateResponse> {
    const formData = new FormData();
    formData.append('operation', payload.operation);
    formData.append('model', payload.model);
    formData.append('target', JSON.stringify(payload.target));
    if (payload.prompt !== undefined) formData.append('prompt', payload.prompt);
    formData.append('mask', payload.mask, 'mask.png');
    return apiClient.post(
      '/ai/image-edits',
      formData,
      withActiveOrgHeader(request)
    ) as Promise<AIJobCreateResponse>;
  },

  async imageUpscale(
    payload: {
      target: ImageUpscaleRequestTarget;
      scale?: string;
      model?: string;
      faceEnhancement?: boolean;
    },
    request?: ActiveOrgRequest
  ) {
    return apiClient.post(
      '/ai/image-upscale',
      {
        target: payload.target,
        scale: payload.scale,
        model: payload.model,
        faceEnhancement: payload.faceEnhancement === true,
      },
      withActiveOrgHeader(request)
    ) as Promise<AIJobCreateResponse>;
  },

  /**
   * 获取音色列表（仅中文）
   * GET /api/v1/ai/voices
   */
  async getVoices(request?: ActiveOrgRequest): Promise<VoicesResponse> {
    const response = (await apiClient.get('/ai/voices', withActiveOrgHeader(request))) as {
      voices?: unknown[];
    };
    const normalizedVoices = (response.voices ?? [])
      .map((voice) => normalizeVoiceItem(voice))
      .filter((voice): voice is VoiceApiItem => Boolean(voice));

    // 筛选支持中文的音色
    const chineseVoices = normalizedVoices.filter(isChineseVoice);

    return {
      voices: chineseVoices,
    };
  },

  async generateImage(
    payload: GenerateImageDto & { model?: string; tagId?: string | null },
    request?: ActiveOrgRequest
  ) {
    const requestBody = {
      prompt: payload.prompt,
      model: payload.model,
      style: payload.style,
      width: payload.width,
      height: payload.height,
      projectId: payload.projectId,
      storyboardId: payload.storyboardId,
      tagId: payload.tagId ?? undefined,
      negativePrompt: payload.negativePrompt,
    };

    return apiClient.post(
      '/ai/generate-image',
      requestBody,
      withActiveOrgHeader(request)
    ) as Promise<AIJobCreateResponse>;
  },

  async generateVideo(
    payload: GenerateVideoDto & {
      model?: string;
      tagId?: string | null;
      fps?: number;
      videoType?: 'text2video' | 'img2video';
    },
    request?: ActiveOrgRequest
  ) {
    const requestBody = {
      videoType: payload.videoType ?? (payload.imageUrl ? 'img2video' : 'text2video'),
      prompt: payload.prompt,
      imageUrl: payload.imageUrl,
      duration: payload.duration,
      fps: payload.fps,
      model: payload.model,
      projectId: payload.projectId,
      storyboardId: payload.storyboardId,
      tagId: payload.tagId ?? undefined,
    };

    return apiClient.post(
      '/ai/generate-video',
      requestBody,
      withActiveOrgHeader(request)
    ) as Promise<AIJobCreateResponse>;
  },

  async generateSoundEffect(
    payload: {
      description: string;
      duration?: number;
      effectType?: string;
      effectName?: string;
      model?: string;
      projectId?: string;
      storyboardId?: string;
      promptInfluence?: number;
      loop?: boolean;
    },
    request?: ActiveOrgRequest
  ) {
    return apiClient.post(
      '/ai/generate-sound-effect',
      payload,
      withActiveOrgHeader(request)
    ) as Promise<AIJobCreateResponse>;
  },

  async getJobStatus(jobId: string, request?: ActiveOrgRequest) {
    return apiClient.get(
      `/ai/jobs/${jobId}`,
      withActiveOrgHeader(request)
    ) as Promise<AIJobStatusPayload>;
  },

  async getJobsStatusBatch(jobIds: string[], request?: ActiveOrgRequest) {
    return apiClient.post(
      '/ai/jobs/batch-status',
      {
        jobIds,
      },
      withActiveOrgHeader(request)
    ) as Promise<Record<string, AIJobStatusPayload>>;
  },

  async getJobs(
    params?: {
      projectId?: string;
      status?: number;
      activeOnly?: boolean;
      tabType?: number;
      page?: number;
      pageSize?: number;
    },
    request?: ActiveOrgRequest
  ) {
    return apiClient.get('/ai/jobs', {
      params,
      ...withActiveOrgHeader(request),
    }) as Promise<AIJobsListResponse>;
  },

  /**
   * 有指令/控制时预估展开后字数（与送模同序）。
   * 调用方应做结构级缓存，避免打字重复请求。
   */
  async estimatePromptChars(
    payload: {
      content: string;
      projectId?: string | null;
      businessParams?: Record<string, unknown>;
      aspectRatio?: string;
    },
    request?: ActiveOrgRequest
  ): Promise<{ expandedLength: number; contentLength: number }> {
    return apiClient.post(
      '/ai/prompt-char-estimate',
      {
        content: payload.content,
        projectId: payload.projectId ?? undefined,
        businessParams: payload.businessParams,
        aspectRatio: payload.aspectRatio,
      },
      withActiveOrgHeader(request)
    ) as Promise<{ expandedLength: number; contentLength: number }>;
  },
};
